import Input from '@bugbug/core/components/Input';
import Select, { SelectOption, SelectOptionHelpText } from '@bugbug/core/components/Select';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import type { ChangeEvent } from 'react';

import type { SwitchContextStep } from '@bugbug/core/types/steps';
import type { StepRun } from '@bugbug/core/types/tests';
import FormField from '~/components/FormField';
import { getInitialValues } from '~/modules/steps/steps.utils';

import { ElementSelectorBuilder } from '../ElementSelectorBuilder/ElementSelectorBuilder';
import { FIELD_NAMES } from '../Interaction/Interaction.constants';

interface SwitchContextFieldsProps {
  stepRun: StepRun;
  context?: 'component' | 'test' | 'testRun';
  readOnly?: boolean;
}

export const SwitchContextFields = ({ context, readOnly, stepRun }: SwitchContextFieldsProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'stepDetails.switchContextFields' });
  const formik = useFormikContext<SwitchContextStep>();
  const valueFieldProps = formik.getFieldProps(FIELD_NAMES.VALUE);

  const renderSelectedOption = (option) => (option.label ? <div>{option.label[0]}</div> : '');

  const handleContextChange = (
    event: ChangeEvent<HTMLSelectElement & { value: SwitchContextStep['value'] }>,
  ) => {
    valueFieldProps.onChange(event);

    if (event.target.value === 'topFrame') {
      formik.setFieldValue(FIELD_NAMES.SELECTORS, []);
    } else {
      formik.setValues(
        getInitialValues<SwitchContextStep>({
          ...formik.values,
          value: event.target.value,
        } as Partial<SwitchContextStep>),
      );
    }
  };

  return (
    <>
      <FormField label={t('tabNumber', 'Tab number')} labelId={FIELD_NAMES.TAB_NO}>
        <Input
          fullWidth
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...formik.getFieldProps(FIELD_NAMES.TAB_NO)}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          readOnly={readOnly}
        />
      </FormField>
      <FormField label={t('context.label', 'Context')}>
        <Select
          {...valueFieldProps}
          onChange={handleContextChange}
          disabled={readOnly}
          renderSelectedOption={renderSelectedOption}
        >
          <SelectOption value="topFrame">
            <div>{t('context.options.topFrame.label', 'Top')}</div>
            <SelectOptionHelpText>
              {t(
                'context.options.topFrame.description',
                "Main context of the tab. It's the top-level container of everything on the page.",
              )}
            </SelectOptionHelpText>
          </SelectOption>
          <SelectOption value="iframe">
            <div>{t('context.options.iframe.label', 'Iframe')}</div>
            <SelectOptionHelpText>
              {t('context.options.iframe.description', 'A nested browser context.')}
            </SelectOptionHelpText>
          </SelectOption>
        </Select>
      </FormField>
      {formik.values.value !== 'topFrame' && (
        <ElementSelectorBuilder
          label={t('framePath', 'Frame element')}
          context={context}
          computedValue={stepRun.computedSelector}
          disabled={readOnly}
          relationDisabled
        />
      )}
    </>
  );
};
